/**
 * Camera sheet for attaching photos to a Flux Agent message: a live rear-camera preview with a
 * shutter, a review step with Retake and Use photo, and a library picker. On devices without
 * `getUserMedia`, or when camera access is refused, it offers the system camera
 * (`<input capture="environment">`) and the photo library instead. Every photo is shrunk and
 * re-encoded before it is handed to `onPhotos`, which strips EXIF and location.
 *
 * @module apps/web/src/components/flux-agent/FluxAgentCameraSheet
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, ImagePlus, Loader2, RotateCcw, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { canUseCamera, encodeFrame, preparePickedPhoto } from '@/lib/fluxAgentPhoto';
import { loadFluxAgentBrowser, photoPolicy } from '@/lib/fluxAgentBrowser';

/** Inputs for {@link FluxAgentCameraSheet}. */
export interface FluxAgentCameraSheetProps {
  /** Whether the sheet is shown. */
  open: boolean;
  /** Called when the sheet should close; the camera stream is stopped first. */
  onOpenChange: (open: boolean) => void;
  /** Receives prepared JPEG photos to attach. */
  onPhotos: (photos: Blob[]) => void;
  /** How many more photos the message can take; the library picker stops there. */
  remaining: number;
}

/** Where the sheet is: starting the camera, previewing, reviewing a shot, or without a camera. */
type Stage = 'starting' | 'live' | 'review' | 'fallback';

/** Render the capture sheet. The camera runs only while the sheet is open. */
export function FluxAgentCameraSheet({
  open,
  onOpenChange,
  onPhotos,
  remaining,
}: FluxAgentCameraSheetProps) {
  const video = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream | null>(null);
  const generation = useRef(0);
  const captureInput = useRef<HTMLInputElement>(null);
  const libraryInput = useRef<HTMLInputElement>(null);
  const [stage, setStage] = useState<Stage>('starting');
  const [shot, setShot] = useState<{ blob: Blob; url: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const stop = useCallback(() => {
    generation.current += 1;
    stream.current?.getTracks().forEach(track => track.stop());
    stream.current = null;
  }, []);

  const start = useCallback(async () => {
    const attempt = ++generation.current;
    setError(null);
    if (!canUseCamera()) {
      setStage('fallback');
      return;
    }
    setStage('starting');
    try {
      const media = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1440 },
        },
        audio: false,
      });
      if (generation.current !== attempt) {
        media.getTracks().forEach(track => track.stop());
        return;
      }
      stream.current = media;
      if (video.current) {
        video.current.srcObject = media;
        await video.current.play().catch(() => undefined);
      }
      if (generation.current === attempt) setStage('live');
    } catch (cause) {
      if (generation.current !== attempt) return;
      const denied = cause instanceof DOMException && cause.name === 'NotAllowedError';
      setError(
        denied
          ? 'Camera access is off for this site. Use the buttons below, or allow the camera in your browser settings.'
          : 'The camera could not start. Use the buttons below instead.'
      );
      setStage('fallback');
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    void loadFluxAgentBrowser().catch(() => undefined);
    void start();
    return () => {
      stop();
      setShot(current => {
        if (current) URL.revokeObjectURL(current.url);
        return null;
      });
    };
  }, [open, start, stop]);

  const takeShot = async () => {
    const attempt = generation.current;
    const element = video.current;
    if (!element || !element.videoWidth) return;
    setBusy(true);
    try {
      const blob = await encodeFrame(element, element.videoWidth, element.videoHeight);
      if (generation.current !== attempt) return;
      setShot({ blob, url: URL.createObjectURL(blob) });
      setStage('review');
    } catch (cause) {
      if (generation.current === attempt)
        setError(cause instanceof Error ? cause.message : 'The photo could not be taken.');
    } finally {
      setBusy(false);
    }
  };

  const retake = () => {
    if (shot) URL.revokeObjectURL(shot.url);
    setShot(null);
    setStage(stream.current ? 'live' : 'fallback');
  };

  const usePhoto = () => {
    if (!shot) return;
    onPhotos([shot.blob]);
    URL.revokeObjectURL(shot.url);
    setShot(null);
    onOpenChange(false);
  };

  const pickFiles = async (files: File[]) => {
    const attempt = generation.current;
    setBusy(true);
    try {
      const policies = await Promise.all(
        files.map(file => photoPolicy({ name: file.name, mimeType: file.type, size: file.size }))
      );
      if (generation.current !== attempt) return;
      const images = files
        .filter((_, index) => policies[index].pickable)
        .slice(0, Math.max(0, remaining));
      if (!images.length) {
        setError('Choose a photo (JPEG, PNG, WebP or HEIC).');
        return;
      }
      const prepared = await Promise.all(images.map(preparePickedPhoto));
      if (generation.current !== attempt) return;
      onPhotos(prepared);
      onOpenChange(false);
    } catch {
      if (generation.current === attempt)
        setError('That photo could not be opened in this browser.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!next) stop();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-xl" data-testid="flux-agent-camera-sheet">
        <DialogHeader>
          <DialogTitle>Take a photo</DialogTitle>
          <DialogDescription>
            Flux Agent sees the photo with your message. It is shrunk and its location data is
            removed before it is sent.
          </DialogDescription>
        </DialogHeader>

        <div className="relative overflow-hidden rounded-lg bg-black">
          <video
            ref={video}
            className={
              stage === 'live' || stage === 'starting'
                ? 'aspect-[4/3] w-full object-cover'
                : 'hidden'
            }
            playsInline
            muted
            aria-label="Camera preview"
          />
          {stage === 'starting' && (
            <div className="absolute inset-0 flex items-center justify-center text-white">
              <Loader2 className="h-6 w-6 animate-spin" aria-label="Starting the camera" />
            </div>
          )}
          {stage === 'review' && shot && (
            <img
              src={shot.url}
              alt="The photo you just took"
              className="aspect-[4/3] w-full object-contain"
            />
          )}
          {stage === 'fallback' && (
            <div className="flex aspect-[4/3] w-full items-center justify-center p-6 text-center text-sm text-white/80">
              {error || 'This browser can’t show a live camera here.'}
            </div>
          )}
        </div>
        {error && stage !== 'fallback' && (
          <p className="text-destructive text-sm" role="alert">
            {error}
          </p>
        )}

        <input
          ref={captureInput}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          tabIndex={-1}
          data-testid="flux-agent-camera-capture-input"
          onChange={event => {
            void pickFiles(Array.from(event.target.files || []));
            event.target.value = '';
          }}
        />
        <input
          ref={libraryInput}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          tabIndex={-1}
          data-testid="flux-agent-photo-library-input"
          onChange={event => {
            void pickFiles(Array.from(event.target.files || []));
            event.target.value = '';
          }}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => libraryInput.current?.click()}
            disabled={busy || remaining <= 0}
          >
            <ImagePlus className="mr-1.5 h-4 w-4" /> Choose photos
          </Button>
          {stage === 'review' ? (
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={retake}>
                <RotateCcw className="mr-1.5 h-4 w-4" /> Retake
              </Button>
              <Button type="button" onClick={usePhoto}>
                <Check className="mr-1.5 h-4 w-4" /> Use photo
              </Button>
            </div>
          ) : stage === 'fallback' ? (
            <Button
              type="button"
              onClick={() => captureInput.current?.click()}
              disabled={busy || remaining <= 0}
            >
              <Camera className="mr-1.5 h-4 w-4" /> Open camera
            </Button>
          ) : (
            <Button
              type="button"
              onClick={() => void takeShot()}
              disabled={stage !== 'live' || busy}
              aria-label="Take photo"
            >
              {busy ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <Camera className="mr-1.5 h-4 w-4" />
              )}
              Take photo
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
